import { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useTheme } from "../theme/ThemeContext.jsx";

const REFRESH_MS = 30000;
const NHL_PLAYER_BASE = "https://players.brightcove.net/6415718365001/EXtG1xJ7H_default/index.html";

const VideoFrame = ({ clipId, title }) => (
  <div style={{ position: "relative", paddingTop: "56.25%" }}>
    <iframe
      title={title}
      src={`${NHL_PLAYER_BASE}?videoId=${clipId}`}
      allowFullScreen
      allow="autoplay; fullscreen; encrypted-media"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
    />
  </div>
);

const ClipHighlight = ({ clip, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [clip.clipId]);

  return (
    <div ref={ref} className="mb-3 p-2 border rounded">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>{clip.title}</strong>
        <button type="button" className="btn-close" aria-label="Close highlight" onClick={onClose} />
      </div>
      <VideoFrame clipId={clip.clipId} title={clip.title} />
    </div>
  );
};

const ExtraHighlights = ({ saves = [], fights = [], fightClips = [], recap, condensed, onPlay }) => {
  const playableSaves = saves.filter((clip) => /^\d+$/.test(clip.clipId));
  const playableFightClips = fightClips.filter((clip) => /^\d+$/.test(clip.clipId));
  const fullGame = [
    recap && { ...recap, label: "Game recap" },
    condensed && { ...condensed, label: "Condensed game" },
  ].filter((clip) => clip && /^\d+$/.test(clip.clipId));
  if (!fullGame.length && !playableSaves.length && !fights.length) return null;

  return (
    <div className="mb-3 p-2 border rounded">
      {fullGame.length > 0 && (
        <div className="mb-2">
          <strong>Full game</strong>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {fullGame.map((clip) => (
              <button key={clip.clipId} type="button" className="btn btn-sm btn-primary" onClick={() => onPlay({ ...clip, title: clip.label })}>
                {clip.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {playableSaves.length > 0 && (
        <div className="mb-2">
          <strong>Saves</strong>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {playableSaves.map((clip) => (
              <button key={clip.clipId} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onPlay(clip)}>
                {clip.title.replace(/^[A-Z]{2,3}@[A-Z]{2,3}:\s*/, "")}
              </button>
            ))}
          </div>
        </div>
      )}
      {fights.length > 0 && (
        <div>
          <strong>Fights</strong>
          <ul className="list-unstyled mb-0 mt-1">
            {fights.map((fight) => (
              <li key={`${fight.period}-${fight.time}`}>
                {fight.players.join(" vs ") || "Fight"}
                <span className="text-muted"> · Period {fight.period} · {fight.time}</span>
              </li>
            ))}
          </ul>
          {playableFightClips.length > 0 ? (
            <div className="d-flex flex-wrap gap-1 mt-1">
              {playableFightClips.map((clip) => (
                <button key={clip.clipId} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onPlay(clip)}>
                  {clip.title}
                </button>
              ))}
            </div>
          ) : (
            <small className="text-muted">The NHL hasn't posted a video of this fight.</small>
          )}
        </div>
      )}
    </div>
  );
};

const GoalHighlight = ({ highlight, onSelect, onClose }) => {
  const { player, index } = highlight;
  const goals = player.goalClips;
  const goal = goals[index];
  const hasClip = goal.clipId && /^\d+$/.test(goal.clipId);
  const shareUrl = goal.sharingUrl?.startsWith("https://nhl.com/") ? goal.sharingUrl : null;
  const when = goal.period && goal.time ? `Period ${goal.period} · ${goal.time}` : "";

  return (
    <div className="mb-3 p-2 border rounded">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>
          {player.name} · goal{goal.goalNumber ? ` #${goal.goalNumber}` : ""}
          {when && <span className="text-muted"> · {when}</span>}
        </strong>
        <button type="button" className="btn-close" aria-label="Close highlight" onClick={onClose} />
      </div>
      {goals.length > 1 && (
        <div className="btn-group btn-group-sm mb-2" role="group" aria-label={`${player.name}'s goals`}>
          {goals.map((item, i) => (
            <button
              key={`${item.period}-${item.time}`}
              type="button"
              className={`btn ${i === index ? "btn-primary" : "btn-outline-primary"}`}
              aria-pressed={i === index}
              onClick={() => onSelect(i)}
            >
              Goal {item.goalNumber ?? i + 1}
            </button>
          ))}
        </div>
      )}
      {hasClip ? (
        <VideoFrame clipId={goal.clipId} title={`${player.name} goal highlight`} />
      ) : (
        <p className="mb-0">The highlight for this goal isn't available yet. Check back shortly.</p>
      )}
      {shareUrl && (
        <a className="d-inline-block mt-2" href={shareUrl} target="_blank" rel="noopener noreferrer">
          Watch on NHL.com
        </a>
      )}
    </div>
  );
};

const TeamTable = ({ team, onSelectGoal }) => (
  <div className="mb-4">
    <h6>
      {team.abbrev} - {team.score} goals, {team.shotsOnGoal} shots
    </h6>
    <div className="table-responsive">
      <table className="table table-sm table-striped mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>Player</th>
            <th>TOI</th>
            <th>SOG</th>
            <th>G</th>
            <th>A</th>
            <th>PIM</th>
          </tr>
        </thead>
        <tbody>
          {team.skaters.map((p) => {
            const canWatch = p.goals > 0 && p.goalClips?.length > 0;
            const watch = () => onSelectGoal(p);
            return (
              <tr key={p.id}>
                <td>{p.number}</td>
                <td className="text-start">
                  {canWatch ? (
                    <button type="button" className="btn btn-link p-0 text-start" onClick={watch} title="Watch latest goal">
                      {p.name}
                    </button>
                  ) : (
                    p.name
                  )}
                </td>
                <td>{p.toi}</td>
                <td>{p.shots}</td>
                <td>
                  {canWatch ? (
                    <button type="button" className="btn btn-link p-0" onClick={watch} aria-label={`Watch ${p.name}'s latest goal`} title="Watch latest goal">
                      {p.goals}
                    </button>
                  ) : (
                    p.goals
                  )}
                </td>
                <td>{p.assists}</td>
                <td>{p.penaltyMinutes}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const LiveGameStatsModal = ({ show, onClose, gameId, title }) => {
  const { theme } = useTheme();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [clip, setClip] = useState(null);

  useEffect(() => {
    setHighlight(null);
    setClip(null);
    if (!show || !gameId) return undefined;
    let cancelled = false;

    const load = async () => {
      const { data, error: fnError } = await supabase.functions.invoke("nhl-boxscore", {
        body: { gameId },
      });
      if (cancelled) return;
      if (fnError || data?.error) {
        setError("Unable to load live stats.");
      } else {
        setError("");
        setStats(data);
      }
      setLoading(false);
    };

    setLoading(true);
    setStats(null);
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [show, gameId]);

  if (!show) return null;

  const isDark = theme === "dark";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} live stats`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: isDark ? "#222" : "#fff",
          color: isDark ? "#fff" : "#000",
          border: `1px solid ${isDark ? "#444" : "#ddd"}`,
          borderRadius: 8,
          padding: "1rem",
          width: "min(95vw, 720px)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{title} - Live Stats</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        {stats && (
          <p className="mb-3">
            Period {stats.period} {stats.clock?.timeRemaining}
          </p>
        )}
        {loading && <p>Loading...</p>}
        {error && <p className="text-danger">{error}</p>}
        {highlight && (
          <GoalHighlight
            highlight={highlight}
            onSelect={(index) => setHighlight((current) => ({ ...current, index }))}
            onClose={() => setHighlight(null)}
          />
        )}
        {stats && (
          <div className={isDark ? "table-dark" : ""}>
            <TeamTable team={stats.awayTeam} onSelectGoal={(p) => { setClip(null); setHighlight({ player: p, index: p.goalClips.length - 1 }); }} />
            <TeamTable team={stats.homeTeam} onSelectGoal={(p) => { setClip(null); setHighlight({ player: p, index: p.goalClips.length - 1 }); }} />
          </div>
        )}
        {clip && <ClipHighlight clip={clip} onClose={() => setClip(null)} />}
        {stats && (
          <ExtraHighlights
            saves={stats.saves}
            fights={stats.fights}
            fightClips={stats.fightClips}
            recap={stats.recap}
            condensed={stats.condensed}
            onPlay={(selected) => {
              setHighlight(null);
              setClip(selected);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LiveGameStatsModal;
